/**
 * Honeybadger Error Filtering Rules
 *
 * This module contains all the logic for determining which errors should be
 * reported to Honeybadger and which should be ignored. Each filter includes
 * documentation explaining why it exists and links to relevant GitHub issues.
 *
 * @memberof @albert-io/notifier
 */

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if an error notice is from an API error response
 * API errors are already handled by the application's error handling system
 */
const isApiError = (notice) => {
  const message = notice.message || '';
  return (
    message.includes('"errors":[') && message.includes('"code":') && message.includes('"status":')
  );
};

/**
 * Check if current URL path includes a given string
 */
export const isCurrentUrlMatch = (match) => {
  if (!match) return false;
  const {pathname} = window.location;
  return pathname.includes(match);
};

// ============================================================================
// ERROR FILTERING RULES
// ============================================================================

/**
 * Determine if an error should be reported to Honeybadger
 *
 * @param {Object} notice - The Honeybadger notice object
 * @returns {boolean} - true to report the error, false to suppress it
 */
export const shouldReportError = (notice) => {
  const message = notice.message || '';
  const userAgent = navigator.userAgent || '';
  const isIos = /iPad|iPhone|iPod/.test(userAgent);

  // Ignore UnhandledPromiseRejectionWarning: Unspecified reason
  // These are transient errors that don't provide actionable information
  // https://docs.honeybadger.io/lib/javascript/guides/reducing-noise/#transient-unhandledpromiserejectionwarning-errors
  if (/UnhandledPromiseRejectionWarning: Unspecified reason/.test(message)) {
    return false;
  }

  // Ignore "UnhandledPromiseRejectionWarning" when no additional info is available
  // These are transient errors that don't provide actionable information
  if (/UnhandledPromiseRejectionWarning: {}/.test(message)) {
    return false;
  }

  // Ignore state machine transition errors - timing/race condition issues
  // These occur when state machine events are triggered in unexpected states
  // Usually from async operations in assignment flows that aren't critical
  // https://app.honeybadger.io/projects/121794/faults/121074677
  if (/UnhandledPromiseRejectionWarning:.*No transition: from \d+ event \d+/.test(message)) {
    return false;
  }

  // Ignore UnhandledPromiseRejectionWarning: Object Not Found errors
  // These are expected when users navigate away during data fetching
  if (/UnhandledPromiseRejectionWarning: Object Not Found Matching Id/.test(message)) {
    return false;
  }

  // Ignore API errors - these are handled by application error handling
  // We show user-friendly messages and don't need to track them in Honeybadger
  // https://github.com/albert-io/nexus/issues/13502
  if (message && isApiError(notice)) {
    return false;
  }

  // Ignore Mandark API error responses - backend errors already handled
  // These are server-side errors that show appropriate user messages
  // https://app.honeybadger.io/projects/121794/faults/121063145
  if (/Mandark API error response|Failed to enroll student using relationship/.test(message)) {
    return false;
  }

  // Ignore JSXGraph error
  // Known issue with the JSXGraph library when manipulating DOM elements
  // https://github.com/albert-io/nexus/issues/13401
  if (
    notice.name === 'TypeError' &&
    message.includes("Cannot read properties of null (reading 'insertBefore')")
  ) {
    return false;
  }

  // Ignore SyntaxError: Unexpected token '<'
  // Usually indicates HTML returned instead of expected JSON/JS (CDN issues, etc.)
  // These are typically network/infrastructure issues, not code bugs
  if (notice.name === 'SyntaxError' && message.includes("Unexpected token '<'")) {
    return false;
  }

  // Ignore generic syntax errors - typically from bots or network issues
  // These include "Unexpected end of input", "Unexpected token", etc.
  // Often from crawlers that don't properly execute JS or corrupted file transfers
  // https://app.honeybadger.io/projects/121794/faults/108517488
  if (
    notice.name === 'SyntaxError' &&
    (message.includes('Unexpected end of input') ||
      message.includes('Unexpected token') ||
      message.includes('has already been declared'))
  ) {
    return false;
  }

  // Ignore errors from third-party services

  // List of third-party domains/patterns to ignore
  const thirdPartyPatterns = [
    'js.jam.dev', // Jam.dev screen recording tool
    'asset.goguardian', // GoGuardian school monitoring software
    'google.com', // Google services (GTM/GA/etc)
    'chrome-extension:', // Browser extensions
    'leadbooster-chat.pipedrive.com' // Pipedrive chat widget
  ];

  // Check stack trace for third-party origins
  if (notice.stack) {
    const hasThirdPartyError = thirdPartyPatterns.some((pattern) => notice.stack.includes(pattern));
    if (hasThirdPartyError) {
      return false;
    }
  }

  // Check message for third-party origins as well
  if (message) {
    const hasThirdPartyError = thirdPartyPatterns.some((pattern) => message.includes(pattern));
    if (hasThirdPartyError) {
      return false;
    }
  }

  // Ignore Slate error - will be fixed when upgrading to React 18
  // Slate editor library has known issues with React 16
  // https://github.com/albert-io/nexus/issues/13713#issuecomment-3428391193
  if (message.includes('Cannot resolve a Slate point from DOM point')) {
    return false;
  }

  // Ignore iOS max call stack error — iOS WebKit recursive rendering issue.
  // Affects iPhone, iPad, and iPod across views (assignments, classes, dashboard).
  // Timeboxed investigation in #13786 could not reproduce. WebKit unwinds the
  // stack on `Maximum call stack size exceeded`, so backtraces arrive empty and
  // source maps cannot pinpoint the recursion site. Initial filter only caught
  // iPad; ~91% of recent notices on this fault now come from iPhone.
  // https://github.com/albert-io/nexus/issues/13786
  // https://github.com/albert-io/nexus/issues/17637
  // https://app.honeybadger.io/projects/121794/faults/121578983
  if (message.includes('Maximum call stack size exceeded') && isIos) {
    return false;
  }

  // Ignore audio player AbortError - expected behavior when audio stops
  // When users stop audio playback, the browser throws an AbortError
  // This is expected behavior and not a bug
  // https://github.com/albert-io/nexus/issues/13438#event-19591642041
  if (notice.name === 'AbortError' && message.includes('aborted')) {
    const hasAudioPlayer = !!global.document.getElementsByClassName('audio-player__button').length;
    return !hasAudioPlayer; // Only ignore if audio player is present
  }

  // Ignore terminated requests - network issues, not application errors
  // Occurs when users navigate away during pending requests
  // https://github.com/albert-io/nexus/issues/13714
  if (/Request has been terminated/.test(message)) {
    return false;
  }

  // Ignore timeout errors - network issues beyond application control
  // These occur when API requests exceed timeout thresholds
  // Usually caused by slow network conditions or server-side issues
  // https://app.honeybadger.io/projects/121794/faults/121066843
  // https://app.honeybadger.io/projects/121794/faults/123823242
  if (/Timeout of 60000ms exceeded|Request timeout after 30000ms/.test(message)) {
    return false;
  }

  // Ignore polling cancelled errors - expected behavior when users navigate away
  // Occurs when report polling is intentionally cancelled (e.g., user leaves page)
  // https://app.honeybadger.io/projects/121794/faults/125334411
  if (/Polling cancelled/.test(message)) {
    return false;
  }

  // Ignore generic "Load failed" errors - network issues beyond our control
  // These are low-level browser network errors with no stack trace
  // Caused by user's network conditions, browser issues, or CDN problems
  // https://app.honeybadger.io/projects/121794/faults/108434352
  if (/^UnhandledPromiseRejectionWarning: (TypeError: )?Load failed$/.test(message)) {
    return false;
  }

  // Ignore chunk loading errors - network issues, handled by application
  // These occur during deployments or network issues and are handled gracefully
  // https://github.com/albert-io/nexus/issues/14032
  if (/Loading (CSS )?chunk [0-9]+ failed/.test(message)) {
    return false;
  }

  // Ignore "can't find variable: gmo" error
  // Third-party script issue that we can't control
  if (/Can't find variable: gmo/.test(message)) {
    return false;
  }

  // Ignore browser-injected variable errors - not our application code
  // __gCrWeb is injected by Chrome/Brave iOS, __firefox__ by Firefox mobile
  // $ errors from browser extensions that expect jQuery
  // https://app.honeybadger.io/projects/121794/faults/108702440
  // https://app.honeybadger.io/projects/121794/faults/111439525
  if (
    /Can't find variable: __gCrWeb|Can't find variable: __firefox__/.test(message) ||
    (message.includes('$ is not defined') &&
      notice.stack &&
      notice.stack.includes('chrome-extension:'))
  ) {
    return false;
  }

  // Ignore ResizeObserver errors - benign browser performance warnings
  // This warning occurs when ResizeObserver callbacks take too long to complete
  // It's not an actionable application bug and doesn't affect functionality
  // Chrome 84+ emits "loop completed with undelivered notifications";
  // older Chromium-based browsers emit "loop limit exceeded".
  // https://app.honeybadger.io/projects/121794/faults/109764248
  // https://app.honeybadger.io/projects/121794/faults/130477333
  if (
    /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/.test(message)
  ) {
    return false;
  }

  // Ignore "Failed to execute 'insertBefore' on 'Node'" errors
  // We are assuming that these errors are caused by in-browser translation tools.
  // See: https://github.com/albert-io/nexus/issues/15020#issuecomment-3647360462

  // Additionally, if we think this ignore is too broad we could try to detect whether or not in-browser translation has been used
  // and selectively ignore this error.
  if (
    /Failed to execute 'insertBefore' on 'Node': The node before which the new node is to be inserted is not a child of this node./.test(
      message
    )
  ) {
    return false;
  }

  // Ignore "Failed to execute 'removeChild' on 'Node'" errors
  // Caused by Google Translate manipulating the DOM on chrome books
  // See: https://github.com/albert-io/nexus/issues/14283#issuecomment-3665651331
  if (
    /Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node./.test(
      message
    )
  ) {
    return false;
  }

  // Ignore GraphQL FeatureFlag query errors
  // https://app.honeybadger.io/projects/121794/faults/128962194
  // https://app.honeybadger.io/projects/121794/faults/128962193
  if (/GraphQL (network error|error) in FeatureFlag/.test(message)) {
    return false;
  }

  // Report all other errors
  return true;
};
